import * as React from 'react';
import * as _ from 'lodash';
import { Redirect, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ControlState,
  ControlStyleClass,
  ControlValidation,
  Notification,
  Logging,
  renderEdit,
  API,
  Seller,
  NotificationData,
  ResponseError,
  UserInfo,
  UserContext,
  ContentLoading,
  renderSelect,
  getCountries,
  getStateProvinces,
  DisplayDataStateProvince,
  stateProvinceAbbreviationToObject,
  DisplayData,
  TaxPayer,
  SearchAhead,
  Footer,
  validatePostalCode,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxButton } from '@vertexinc/vtx-ui-react-component-library';
import SellerItemProps from './SellerProps.interface';
import '../common-ecwizard.scss';
import './seller.scss';
import useSeller from './useSeller';
import useTaxpayers from '../Common/useTaxpayers';

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlValidation: ControlValidation = {
  requiredFields: ['seller-code', 'seller-name'],
  minMaxSize: [
    { field: 'seller-code', maxSize: 20 },
    { field: 'seller-name', maxSize: 60 },
    { field: 'seller-address', maxSize: 100 },
    { field: 'seller-city', maxSize: 60 },
  ],
  postalCodeFields: ['seller-postal-zip'],
  customMessages: [],
};

interface ParamTypes {
  uuid: string,
}

const SellerItem = ({
  gatewayEndPoint,
}: SellerItemProps): JSX.Element => {
  // Get the Uuid from the Url Parameters
  const { uuid } = useParams<ParamTypes>();
  const [isLoaded, setIsLoaded] = React.useState(uuid === undefined);

  // Load the Seller if the UUID is specified during an Edit
  const [seller, sellerNotification] = uuid ? useSeller(gatewayEndPoint, uuid) : [null, null];

  const dispatch = useDispatch();

  const [notification, setNotification] = React.useState(null);
  const [redirect, setRedirect] = React.useState(null);

  const [searchTaxpayerName, setSearchTaxpayerName] = React.useState(null);
  const [taxpayers, searchingTaxpayers, taxpayersNotification] = useTaxpayers(gatewayEndPoint, searchTaxpayerName);

  const [sellerName, setSellerName] = React.useState(null);
  const [sellerCode, setSellerCode] = React.useState(null);
  const [taxpayerUuid, setTaxpayerUuid] = React.useState(null); // Stores Taxpayer Uuid
  const [address, setAddress] = React.useState(null);
  const [city, setCity] = React.useState(null);
  const [stateProvince, setStateProvince] = React.useState(null);
  const [postalCode, setPostalCode] = React.useState(null);
  const [country, setCountry] = React.useState(null);

  const getTaxpayers = (): DisplayData[] => {
    if (taxpayers) {
      // eslint-disable-next-line arrow-body-style
      return taxpayers.map((item: TaxPayer) => {
        return {
          data: item.uuid,
          display: `${item.taxPayerName} / ${item.partitionName}`,
        };
      });
    }

    // Return the Current Selected TaxPayer
    if (seller?.taxpayerDefinition) {
      return [{
        data: seller.taxpayerDefinition.uuid,
        display: `${seller.taxpayerDefinition.taxPayerName} / ${seller.taxpayerDefinition.partitionName}`,
      }];
    }

    return [];
  };

  const searchTaxpayers = (searchValue: string) => {
    Logging.Debug('Searching Taxpayers', searchValue);
    setSearchTaxpayerName(searchValue);
  };

  // Make the API Call to Add the Seller
  const addSeller = (newSeller: Seller) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Add the Seller
      await api.ecwizard.addSellerV41(newSeller);

      // Direct back to Sellers
      setRedirect('/ecwizard/sellers');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Seller Save Error',
        description: `Unable to Add the Seller '${newSeller.name}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Make the API Call to Edit the Seller
  const editSeller = (updatedSeller: Seller) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Edit the Seller
      await api.ecwizard.editSellerV41(updatedSeller);

      // Direct back to Sellers
      setRedirect('/ecwizard/sellers');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Seller Save Error',
        description: `Unable to Edit the Seller '${updatedSeller.name}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Save Button was Pressed
  const onSave = (): void => {
    // Find the Correct TaxPayer by Uuid
    const taxpayer: TaxPayer = taxpayerUuid && taxpayers ? taxpayers.filter((x) => x.uuid === taxpayerUuid)[0] : seller?.taxpayerDefinition;

    const addEditSeller: Seller = {
      uuid,
      code: sellerCode,
      name: sellerName,
      taxpayerDefinition: taxpayer
        ? {
          uuid: taxpayerUuid,
          partitionName: taxpayer.partitionName,
          departmentCode: taxpayer.departmentCode,
          divisionCode: taxpayer.divisionCode,
          companyCode: taxpayer.companyCode,
          taxPayerName: taxpayer.taxPayerName,
        }
        : null,
      address,
      city,
      stateProvince,
      postalCode,
      country,
    };

    if (uuid) {
      Logging.Info('Edit Seller', addEditSeller);

      dispatch(editSeller(addEditSeller));
    } else {
      Logging.Info('Add Seller', addEditSeller);

      dispatch(addSeller(addEditSeller));
    }
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  const validateValues = (): boolean => {
    if (!sellerCode) {
      return false;
    }

    if (!sellerName) {
      return false;
    }

    if (sellerCode?.length > 20) {
      return false;
    }

    if (sellerName?.length > 60) {
      return false;
    }

    if (address?.length > 100) {
      return false;
    }

    if (city?.length > 60) {
      return false;
    }

    // Validate Postal Code
    if (!validatePostalCode(postalCode, country)) {
      return false;
    }

    return true;
  };

  // Seller was loaded during an Edit
  React.useEffect(() => {
    if (seller) {
      setSellerCode(seller.code);
      setSellerName(seller.name);
      setTaxpayerUuid(seller.taxpayerDefinition?.uuid);
      setAddress(seller.address);
      setCity(seller.city);
      setStateProvince(seller.stateProvince);
      setPostalCode(seller.postalCode);
      setCountry(seller.country);

      setIsLoaded(true);
    }
  }, [seller]);

  // Country was Changed
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // Country was Changed.  Check to see if the State is still Valid
    if (country && stateProvince) {
      const displayDataGeo: DisplayDataStateProvince = stateProvinceAbbreviationToObject(stateProvince);

      // Clear the State if a differnt Country was picked
      if (displayDataGeo && (country !== displayDataGeo.country)) {
        setStateProvince(null);
      }
    } else if (!country && stateProvince) {
      setStateProvince(null);
    }
  }, [country]);

  // State was changed
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // State is Selected but not the Country.
    if (stateProvince && !country) {
      const displayDataGeo: DisplayDataStateProvince = stateProvinceAbbreviationToObject(stateProvince);

      // Picking the Country from the State
      if (displayDataGeo) {
        setCountry(displayDataGeo.country);
      }
    }
  }, [stateProvince]);

  // Unable to Load the Seller during an Edit
  React.useEffect(() => {
    if (sellerNotification) {
      setNotification(sellerNotification || taxpayersNotification);
    }
  }, [sellerNotification, taxpayersNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Seller: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setRedirect(null);
    };
  }, []);

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  const searchTaxpayerNameAhead: SearchAhead = {
    searching: searchingTaxpayers,
    searchingText: 'Searching Taxpayers ...',
    noResultsText: 'No Taxpayers found',
    onSearch: searchTaxpayers,
    debounceMilliseconds: 750,
  };

  // Set the Country used in Validation
  controlValidation.country = country;

  const canSave: boolean = validateValues();

  return (
    <>
      {
        !isLoaded
          ? (
            !sellerNotification
            && (
              <ContentLoading loadingMessage="Loading Seller ..." />
            )
          )
          : (
            <div className="ecwizardContainer">
              <div className="ecwizardHeaderBack">
                <VertexLink href="#/ecwizard/sellers" display="Back to Sellers" />
              </div>
              <div className="ecwizardHeader">
                <div className="ecwizardHeaderLeft">
                  {uuid ? 'Edit Seller' : 'Add Seller'}
                </div>
              </div>
              <div className="ecwizardHeaderDescription">
                Define the seller code, name, basic system information, and address of the seller.
              </div>
              <hr />
              <div className="ecwizardContentControls">
                {renderEdit(33, 'seller-code', 'Seller Code*', 'text', 'Seller Code',
                  sellerCode, setSellerCode, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(33, 'seller-name', 'Seller Name*', 'text', 'Seller Name',
                  sellerName, setSellerName, controlStateEditable, controlStyle, controlValidation)}
                {renderSelect(33, 'taxpayerUuid', 'Taxpayer', 'Search by Taxpayer', false, false, getTaxpayers(),
                  true, searchTaxpayerNameAhead, taxpayerUuid, setTaxpayerUuid, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(66, 'seller-address', 'Street Address', 'text', null,
                  address, setAddress, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(33, 'seller-city', 'City', 'text', null,
                  city, setCity, controlStateEditable, controlStyle, controlValidation)}
                {renderSelect(33, 'seller-province-state', 'State / Province', 'Select State / Province', false, false,
                  getStateProvinces(country), true, null, stateProvince, setStateProvince, controlStateEditable, controlStyle, controlValidation)}
                {renderEdit(33, 'seller-postal-zip', 'Postal / Zip Code', 'text', null,
                  postalCode, setPostalCode, controlStateEditable, controlStyle, controlValidation)}
                {renderSelect(33, 'seller-country', 'Country', 'Select Country', false, false,
                  getCountries(), true, null, country, setCountry, controlStateEditable, controlStyle, controlValidation)}
              </div>
              <Footer cancelLink="#/ecwizard/sellers" saveEnabled={canSave} onSave={onSave} />
            </div>
          )
      }
      <Notification
        data={notification}
        onNotificationClose={onCloseNotification}
        autoClose
      />
    </>
  );
};

export default SellerItem;
